import { db } from '@/lib/firebase';
import {
  collection,
  getDocs,
  getDoc,
  doc,
  query,
  orderBy,
  limit,
  where,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
} from 'firebase/firestore';

/** Byline on BB's own (in-house) articles. */
export const BB_ADMIN_AUTHOR = "Breakin' Bread Admin";
import type { NewsArticle } from '@/types/news';
import type { NewsTopic } from '@/lib/news-topics';
import { decodeHtmlEntities } from '@/lib/decode-entities';

/** Decode HTML entities in the human-visible text fields (scraped RSS titles
 *  arrive with `&#8217;` etc.). Applied on every read so existing stored docs
 *  display cleanly without a data migration. */
function cleanArticle<T extends Record<string, any>>(a: T): T {
  return {
    ...a,
    ...(a.title ? { title: decodeHtmlEntities(a.title) } : {}),
    ...(a.excerpt ? { excerpt: decodeHtmlEntities(a.excerpt) } : {}),
    ...(a.subtitle ? { subtitle: decodeHtmlEntities(a.subtitle) } : {}),
  };
}

/**
 * Fetch all published news articles, ordered by date
 */
export async function getNewsArticles(maxItems: number = 50): Promise<NewsArticle[]> {
  // Primary: ordered by publishedAt
  try {
    const q = query(
      collection(db, 'news'),
      orderBy('publishedAt', 'desc'),
      limit(maxItems)
    );
    const snapshot = await getDocs(q);
    if (!snapshot.empty) {
      return snapshot.docs.map(doc => cleanArticle({ id: doc.id, ...doc.data() })) as NewsArticle[];
    }
  } catch (error) {
    console.warn('getNewsArticles (ordered) failed, trying fallback:', error);
  }

  // Fallback: no orderBy (avoids missing-index errors), sort client-side
  try {
    const snapshot = await getDocs(collection(db, 'news'));
    const articles = snapshot.docs.map(doc => cleanArticle({ id: doc.id, ...doc.data() })) as NewsArticle[];
    return articles
      .sort((a, b) => {
        const at = (a as any).publishedAt?.toMillis?.() ?? (a as any).publishedAt ?? 0;
        const bt = (b as any).publishedAt?.toMillis?.() ?? (b as any).publishedAt ?? 0;
        return bt - at;
      })
      .slice(0, maxItems);
  } catch (error) {
    console.error('getNewsArticles fallback also failed:', error);
    return [];
  }
}

/**
 * Fetch a single news article by slug
 */
export async function getNewsBySlug(slug: string): Promise<NewsArticle | null> {
  try {
    // First try direct doc ID lookup (slugs are used as doc IDs)
    const docRef = doc(db, 'news', slug);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return cleanArticle({ id: docSnap.id, ...docSnap.data() }) as NewsArticle;
    }

    // Fallback: query by slug field
    const q = query(
      collection(db, 'news'),
      where('slug', '==', slug),
      limit(1)
    );
    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      return null;
    }

    return cleanArticle({ id: snapshot.docs[0].id, ...snapshot.docs[0].data() }) as NewsArticle;
  } catch (error) {
    console.error('Error fetching news by slug:', error);
    return null;
  }
}

/**
 * Fetch a single news article by ID
 */
export async function getNewsById(id: string): Promise<NewsArticle | null> {
  try {
    const docRef = doc(db, 'news', id);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return null;
    }

    return cleanArticle({ id: docSnap.id, ...docSnap.data() }) as NewsArticle;
  } catch (error) {
    console.error('Error fetching news by id:', error);
    return null;
  }
}

// Tech / Business split and the DAW / Open Source topics live in one pure
// module shared with Home and the scraper, so every surface slices alike.
export { newsSubCategory } from '@/lib/news-topics';

/**
 * Stories carrying a topic (DAW / Open Source) — the /news tab loads these on
 * top of the newest-100 list so a topic tab reaches back further than the
 * last couple of days of the busy general feeds.
 */
export async function getNewsByTopic(topic: NewsTopic, maxItems: number = 120): Promise<NewsArticle[]> {
  // Primary: needs the composite index (topics CONTAINS + publishedAt DESC)
  // from firestore.indexes.json.
  try {
    const q = query(
      collection(db, 'news'),
      where('topics', 'array-contains', topic),
      orderBy('publishedAt', 'desc'),
      limit(maxItems)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => cleanArticle({ id: d.id, ...d.data() })) as NewsArticle[];
  } catch (error) {
    console.warn('getNewsByTopic (ordered) failed, trying fallback:', error);
  }
  // Fallback before the index is deployed: array-contains alone uses the
  // automatic single-field index; sort newest-first here.
  try {
    const snapshot = await getDocs(query(collection(db, 'news'), where('topics', 'array-contains', topic), limit(1000)));
    const ms = (v: any): number => v?.toMillis?.() ?? (typeof v === 'string' ? new Date(v).getTime() || 0 : 0);
    return (snapshot.docs.map(d => cleanArticle({ id: d.id, ...d.data() })) as NewsArticle[])
      .sort((a, b) => ms(b.publishedAt) - ms(a.publishedAt))
      .slice(0, maxItems);
  } catch (error) {
    console.error('getNewsByTopic fallback also failed:', error);
    return [];
  }
}

/**
 * Fetch news articles by category
 */
export async function getNewsByCategory(category: string, maxItems: number = 10): Promise<NewsArticle[]> {
  try {
    const q = query(
      collection(db, 'news'),
      where('category', '==', category),
      orderBy('publishedAt', 'desc'),
      limit(maxItems)
    );
    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => cleanArticle({
      id: doc.id,
      ...doc.data(),
    })) as NewsArticle[];
  } catch (error) {
    console.error('Error fetching news by category:', error);
    return [];
  }
}

/**
 * Fetch featured news articles
 */
export async function getFeaturedArticles(maxItems: number = 5): Promise<NewsArticle[]> {
  try {
    const q = query(
      collection(db, 'news'),
      where('isFeatured', '==', true),
      orderBy('publishedAt', 'desc'),
      limit(maxItems)
    );
    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    })) as NewsArticle[];
  } catch (error) {
    console.error('Error fetching featured articles:', error);
    return [];
  }
}

/**
 * Create a new news article
 */
export async function createNewsArticle(data: Omit<NewsArticle, 'id' | 'publishedAt' | 'updatedAt'>): Promise<string> {
  const slug = data.slug || data.title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
  const docRef = await addDoc(collection(db, 'news'), {
    ...data,
    slug,
    publishedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return docRef.id;
}

/**
 * Update an existing news article
 */
export async function updateNewsArticle(id: string, data: Partial<NewsArticle>): Promise<boolean> {
  try {
    const { id: _, ...updateData } = data;
    await updateDoc(doc(db, 'news', id), {
      ...updateData,
      updatedAt: serverTimestamp(),
    });
    return true;
  } catch (error) {
    console.error('Error updating news article:', error);
    return false;
  }
}

/**
 * Delete a news article
 */
export async function deleteNewsArticle(id: string): Promise<boolean> {
  try {
    await deleteDoc(doc(db, 'news', id));
    return true;
  } catch (error) {
    console.error('Error deleting news article:', error);
    return false;
  }
}

/**
 * Get all news articles (including drafts) for admin
 */
export async function getAllNewsArticles(maxItems: number = 100): Promise<NewsArticle[]> {
  try {
    const q = query(
      collection(db, 'news'),
      orderBy('publishedAt', 'desc'),
      limit(maxItems)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() })) as NewsArticle[];
  } catch (error) {
    console.error('Error fetching all news:', error);
    try {
      const snapshot = await getDocs(collection(db, 'news'));
      return snapshot.docs.map(d => ({ id: d.id, ...d.data() })) as NewsArticle[];
    } catch { return []; }
  }
}
